/* eslint-disable @typescript-eslint/no-explicit-any */

import { debounce } from 'es-toolkit/compat';
import { noop } from 'es-toolkit';
import { useRef } from 'react';
import ReactDOM from 'react-dom';

import { useMountEffect } from './useMountEffect';

import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';

function getContainer(container: React.ReactInstance | (() => React.ReactInstance | null) | null) {
  container = typeof container === 'function' ? container() : container;
  return ReactDOM.findDOMNode(container);
}

export interface ClickToScrollOffsets {
  /**
   * default offset
   */
  offset?: number;
  /**
   * offset at the theme's sm breakpoint
   */
  offsetSm?: number;
  /**
   * offset at the theme's md breakpoint
   */
  offsetMd?: number;
}

interface UseClickToScrollEffectArgs<T> extends ClickToScrollOffsets {
  onChange?: (value: T) => void;
  selector: (eventCurrentTargetId: T) => string;
  container?: React.ReactInstance | (() => React.ReactInstance | null) | null;
  /**
   *
   * Called after scroll. By default it finds the first focusable element in the
   * section that is scrolled to.
   *
   * @param value the value that caused the scroll
   * @param el the container element that was scrolled to
   */
  afterScroll?(value: T, el: HTMLElement): void;
}

function defaultAfterScroll<T>(value: T, el: HTMLElement): void {
  setTimeout(() => {
    // Selector for focusable elements
    // https://stackoverflow.com/a/30753870/2147762
    const elements = el.querySelectorAll<HTMLElement>(`a[href]:not([tabindex='-1']),
      area[href]:not([tabindex='-1'])
      input:not([disabled]):not([tabindex='-1']),
      select:not([disabled]):not([tabindex='-1']),
      textarea:not([disabled]):not([tabindex='-1']),
      button:not([disabled]):not([tabindex='-1']),
      iframe:not([tabindex='-1']),
      [tabindex]:not([tabindex='-1']),
      [contentEditable=true]:not([tabindex='-1'])`);

    for (const element of Array.from(elements)) {
      if ('focus' in element) {
        element.focus();
        break;
      }
    }
  }, 17);
}

export function useClickToScrollEffect<T = string>({
  afterScroll = defaultAfterScroll,
  container,
  offset,
  offsetMd,
  offsetSm,
  onChange = noop,
  selector,
}: UseClickToScrollEffectArgs<T>): {
  handleChange: (value: T) => void;
  value?: T;
} {
  const containerRef = useRef<any>();
  const theme = useTheme();
  const matchesSm = useMediaQuery(theme.breakpoints.up('sm'));
  const matchesMd = useMediaQuery(theme.breakpoints.up('md'));

  useMountEffect(() => {
    if (container) {
      const foundContainer = getContainer(container);
      // TODO throw error if not production
      if (foundContainer) {
        containerRef.current = foundContainer;
      } else {
        containerRef.current = window;
      }
    } else {
      // Usages that will never occur in widget can omit passing a container
      containerRef.current = window;
    }
  });

  let mediaOffset = 0;
  if (matchesMd && offsetMd) {
    mediaOffset = offsetMd;
  } else if (matchesSm && offsetSm) {
    mediaOffset = offsetSm;
  } else if (offset) {
    mediaOffset = offset;
  }

  const handleChange = (value: T): void => {
    if (!value || !containerRef.current) {
      return;
    }
    const listEl = document.getElementById(selector(value));
    if (!listEl) {
      return;
    }
    const listElRect = listEl.getBoundingClientRect();
    const yOffset = container ? (containerRef.current.offsetTop as any) : window.pageYOffset;
    const body = document.querySelector('body');
    const inNativeApp = body ? body.classList.contains('Body-native') : false;
    const nativeOffset = inNativeApp ? 20 : 0;

    const finalOffset = container
      ? listEl.offsetTop - mediaOffset
      : (listElRect?.top ?? 0) + yOffset - mediaOffset - nativeOffset;

    const onScroll = () => {
      if (
        (container ? containerRef.current.offsetTop : window.pageYOffset) ===
        Math.floor(finalOffset)
      ) {
        containerRef.current.removeEventListener('scroll', debouncedOnScroll);
        if (listEl) {
          afterScroll(value, listEl);
          return;
        }
      }
    };

    const debouncedOnScroll = debounce(onScroll, 20);

    containerRef.current.addEventListener('scroll', debouncedOnScroll);
    containerRef.current.scrollTo({
      left: 0,
      top: finalOffset,
      behavior: 'smooth',
    });

    if (onChange) {
      onChange(value);
    }
  };

  return {
    handleChange,
  };
}
